import React, { useState } from 'react'
import intl from 'react-intl-universal'
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Form,
  FormGroup,
} from 'reactstrap'

import { PicklistItem } from '~/types'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./LinkListSubtypesConfigurationModal.styles.scss')

type SubtypeConfigWithNewId = PicklistItem & { newId?: string }

interface ModalState {
  open: boolean
  edit: boolean
  entry: PicklistItem
}

interface Props {
  toggle: () => void
  modalState: ModalState
  existingIds: Array<string>
  onSubmit: (updatedData: SubtypeConfigWithNewId, edit: boolean) => void
}

export default function LinkListSubtypesConfigurationModal(props: Props) {
  const [idValue, setIdValue] = useState(props.modalState.entry.id)
  const [nameValue, setNameValue] = useState(props.modalState.entry.text)
  const [formErrors, setFormErrors] = useState({
    nameLength: false,
    idLength: false,
    idExists: false,
  })

  const validateForm = () => {
    // If either field is empty
    if (!idValue.length || !nameValue.length) {
      setFormErrors({
        ...formErrors,
        idLength: !idValue,
        nameLength: !nameValue,
      })
      return false
    }
    // If the id already exists, and doesn't match the starting value
    if (
      props.existingIds.includes(idValue) &&
      idValue !== props.modalState.entry.id
    ) {
      setFormErrors({ ...formErrors, idExists: true })
      return false
    }
    return true
  }

  const handleSubmit = () => {
    const newSubtype = {
      id: idValue,
      text: nameValue,
      active: true,
      default: false,
    }
    const updatedData = props.modalState.edit
      ? { ...props.modalState.entry, text: nameValue, newId: idValue }
      : newSubtype

    const isValid = validateForm()
    if (isValid) {
      props.onSubmit(updatedData, props.modalState.edit)
      props.toggle()
    }
  }
  return (
    <Modal
      data-testid={`${props.modalState.edit ? 'edit' : 'new'}-subtype-modal`}
      wrapClassName="bootstrap4"
      size="lg"
      className={styles.modal}
      centered
      isOpen
      toggle={props.toggle}
    >
      <Form>
        <ModalHeader>
          {props.modalState.edit
            ? intl.get('LinkListSubtypesConfigurationModal.editTitle')
            : intl.get('LinkListSubtypesConfigurationModal.addTitle')}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>
              {intl.get('LinkListSubtypesConfigurationModal.nameLabel')}
            </Label>
            <Input
              data-testid="name-input-field"
              type="text"
              onChange={(e) => setNameValue(e.target.value)}
              value={nameValue}
            />
            {formErrors.nameLength && (
              <p
                data-testid="name-length-error"
                className="error-message text-danger"
              >
                {intl.get(
                  'LinkListSubtypesConfigurationModal.formError.nameLength'
                )}
              </p>
            )}
          </FormGroup>
          <FormGroup>
            <Label>
              {intl.get('LinkListSubtypesConfigurationModal.idLabel')}
            </Label>
            <Input
              data-testid="id-input-field"
              type="text"
              onChange={(e) => setIdValue(e.target.value)}
              value={idValue}
            />
            {formErrors.idLength && (
              <p
                data-testid="id-length-error"
                className="error-message text-danger"
              >
                {intl.get(
                  'LinkListSubtypesConfigurationModal.formError.idLength'
                )}
              </p>
            )}
            {formErrors.idExists && (
              <p
                data-testid="id-exists-error"
                className="error-message text-danger"
              >
                {intl.get(
                  'LinkListSubtypesConfigurationModal.formError.idExists'
                )}
              </p>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            data-testid="cancel-button"
            color="secondary"
            outline
            onClick={props.toggle}
          >
            {intl.get('LinkListSubtypesConfigurationModal.cancelBtn')}
          </Button>
          <Button
            data-testid={`${
              props.modalState.edit ? 'edit' : 'new'
            }-subtype-submit`}
            color="primary"
            onClick={handleSubmit}
          >
            {props.modalState.edit
              ? intl.get('LinkListSubtypesConfigurationModal.editBtn')
              : intl.get('LinkListSubtypesConfigurationModal.addBtn')}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}
